import axios from 'axios';

// Warn if environment variables are missing
if (!process.env.REACT_APP_API_URL) {
  console.warn('[API] REACT_APP_API_URL is not defined, falling back to "/api"');
}
if (!process.env.REACT_APP_CLIENT_URL) {
  console.warn('[API] REACT_APP_CLIENT_URL is not defined, falling back to "/" for redirects');
}

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  withCredentials: true, // Important for session/cookie-based auth if used
});

// 🔍 Enhanced Request Interceptor
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  
  // Debug logging (uncomment when needed)
  if (process.env.NODE_ENV === 'development') {
    console.groupCollapsed(`[API] ${config.method?.toUpperCase()} to ${config.url}`);
    console.log('Token present:', !!token);
    console.log('Request data:', config.data);
    console.groupEnd();
  }

  // Always attach token if available
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Set Content-Type only if not FormData and not already set
  if (!(config.data instanceof FormData) && !config.headers['Content-Type']) {
    config.headers['Content-Type'] = 'application/json';
  }

  // Ensure API endpoints are not cached
  config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
  config.headers['Pragma'] = 'no-cache';
  config.headers['Expires'] = '0';

  return config;
});

// ⚠️ Enhanced Response Interceptor
API.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API] Response from ${response.config.url}`, {
        status: response.status,
        data: response.data
      });
    }
    return response;
  },
  (error) => {
    const originalRequest = error.config;
    const isLoginEndpoint = originalRequest.url.includes('/login');
    const isLogoutEndpoint = originalRequest.url.includes('/logout');

    // Skip retry logic for login/logout endpoints
    if (isLoginEndpoint || isLogoutEndpoint) {
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized (token expired)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      if (process.env.NODE_ENV === 'development') {
        console.warn('[API] 401 Detected - attempting token refresh or redirect');
      }
      
      // Clear expired token
      localStorage.removeItem('token');
      
      // Redirect to login
      window.location.href = process.env.REACT_APP_CLIENT_URL
        ? `${process.env.REACT_APP_CLIENT_URL}/login`
        : '/login';
      
      return Promise.reject(new Error('Session expired. Please log in again.'));
    }

    // Special handling for logout failures
    if (isLogoutEndpoint) {
      console.error('[API] Logout failed:', error);
      // Even if logout API fails, clear local token
      localStorage.removeItem('token');
      window.location.href = process.env.REACT_APP_CLIENT_URL
        ? `${process.env.REACT_APP_CLIENT_URL}/login`
        : '/login';
      return Promise.resolve(); // Resolve instead of reject to prevent error messages
    }

    // For other errors
    if (process.env.NODE_ENV === 'development') {
      console.error(`[API] Error from ${originalRequest.url || 'unknown'}`, {
        status: error.response?.status,
        data: error.response?.data
      });
    }

    return Promise.reject(error);
  }
);

// Helper method for logout specifically
export const logoutUser = async () => {
  try {
    await API.post('/logout', {}, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest' // Explicitly mark as AJAX
      }
    });
  } catch (error) {
    // Errors are handled by the interceptor
  } finally {
    // Always clear client-side state
    localStorage.removeItem('token');
    delete API.defaults.headers.common['Authorization'];
    window.location.href = process.env.REACT_APP_CLIENT_URL
      ? `${process.env.REACT_APP_CLIENT_URL}/login`
      : '/login';
  }
};

export default API;